import { useScrollReveal } from '@/hooks/useScrollReveal';

const steps = [
  {
    step: '01',
    title: 'Intake & Receival',
    description: 'Fresh harvest arrives at the processing centre. Weight, origin, harvest date, and supplier details are logged into the Freshhlock platform.',
    icon: 'ri-inbox-archive-line',
    iconBg: 'bg-secondary-100',
    iconText: 'text-secondary-600',
    accent: 'bg-secondary-500',
  },
  {
    step: '02',
    title: 'Cleaning & Prep',
    description: 'Produce is washed, seafood is de-veined and de-shelled. Visual and automated sorting removes sub-standard product before processing.',
    icon: 'ri-drop-line',
    iconBg: 'bg-primary-100',
    iconText: 'text-primary-600',
    accent: 'bg-primary-500',
  },
  {
    step: '03',
    title: 'Grading & Weighing',
    description: 'Product is graded to A/B/C standards and precision-weighed. Every gram is accounted for — the weight you see on the label is what you get.',
    icon: 'ri-scales-3-line',
    iconBg: 'bg-accent-100',
    iconText: 'text-accent-600',
    accent: 'bg-accent-500',
  },
  {
    step: '04',
    title: 'Vacuum Packaging',
    description: 'Graded, weighed product is sealed in multi-layer vacuum packs. Batch codes, date stamps, and traceability QR codes are applied at this stage.',
    icon: 'ri-secure-payment-line',
    iconBg: 'bg-secondary-100',
    iconText: 'text-secondary-700',
    accent: 'bg-secondary-600',
  },
  {
    step: '05',
    title: 'Freeze & Catalogue',
    description: 'Packaged product enters IQF or blast freezing at -40°C, then is catalogued as a live, tradeable SKU on the Freshhlock Wholesale and Retail platforms.',
    icon: 'ri-snowflake-line',
    iconBg: 'bg-primary-100',
    iconText: 'text-primary-600',
    accent: 'bg-primary-500',
  },
];

export default function ProcessingWorkflow() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 warm-sand-bg">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Processing Workflow
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            How We Process Your Harvest
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            Five precision stages — each one logged, verified, and traceable from intake to SKU.
          </p>
        </div>

        <div className="relative">
          {/* Vertical connector line (desktop) */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-secondary-200 via-primary-200 to-accent-200 -translate-x-1/2"></div>

          <div className="space-y-10 lg:space-y-16">
            {steps.map((item, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <div key={item.step} className={`relative flex flex-col lg:flex-row items-center gap-6 lg:gap-12 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Connector dot */}
                  <div className="hidden lg:flex absolute left-1/2 top-12 -translate-x-1/2 w-4 h-4 rounded-full bg-card-50 border-2 border-primary-300 z-10"></div>

                  {/* Content side */}
                  <div className={`w-full lg:w-[calc(50%-3rem)] ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full ${item.iconBg} ${item.iconText} text-xs font-semibold mb-3`}>
                      Step {item.step}
                    </div>
                    <h3 className="font-heading font-bold text-primary-700 text-xl mb-3">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed max-w-md ${isEven ? 'lg:ml-auto' : ''}">
                      {item.description}
                    </p>
                  </div>

                  {/* Icon side */}
                  <div className={`w-14 h-14 lg:w-16 lg:h-16 rounded-2xl ${item.iconBg} ${item.iconText} flex items-center justify-center shrink-0 z-10 relative animate-icon-float icon-hover-pop`}>
                    <i className={`${item.icon} text-xl lg:text-2xl`}></i>
                  </div>

                  {/* Empty spacer for alternating layout */}
                  <div className="hidden lg:block w-[calc(50%-3rem)]"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}